import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ShieldAlert, RotateCcw, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import TableToolbar, { useVisibleColumns } from "@/components/TableToolbar";

const ALL_COLUMNS = [
  { key: "name", label: "Name", alwaysOn: true },
  { key: "email", label: "Email" },
  { key: "role", label: "Role" },
  { key: "garage", label: "Garage" },
  { key: "jc_week", label: "JC / week" },
  { key: "last_seen", label: "Last seen" },
  { key: "status", label: "Status", alwaysOn: true },
];

const VIEWS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "suspended", label: "Suspended" },
];

const ROLE_FILTERS = [
  { k: "", label: "All roles" },
  { k: "owner", label: "Owner" },
  { k: "advisor", label: "Service Advisor" },
  { k: "mechanic", label: "Mechanic" },
  { k: "storekeeper", label: "Storekeeper" },
  { k: "driver", label: "Driver" },
  { k: "branch_manager", label: "Branch Manager" },
  { k: "cashier", label: "Cashier" },
  { k: "accountant", label: "Accountant" },
];

const StatusPill = ({ active }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
      active ? "bg-[#12B76A]/10 text-[#12B76A]" : "bg-[#e63946]/10 text-[#e63946]"
    }`}
  >
    {active ? "Active" : "Suspended"}
  </span>
);

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all");
  const [roleF, setRoleF] = useState("");
  const [visibleCols, toggleCol] = useVisibleColumns("admin_users", ALL_COLUMNS);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [reason, setReason] = useState("");

  const canModerate = ["platform_owner", "platform_admin", "support_admin"].includes(me?.admin_role);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (view === "active" || view === "suspended") params.status_f = view;
    if (roleF) params.role_f = roleF;
    api.get("/admin/users", { params })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [view, roleF]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const isVisible = (k) => ALL_COLUMNS.find((c) => c.key === k)?.alwaysOn || visibleCols.has(k);

  const suspend = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/admin/users/${suspendTarget.id}/suspend`, { reason });
      toast.success("User suspended");
      setSuspendTarget(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reactivate = async (u) => {
    if (!window.confirm(`Reactivate ${u.name || u.email}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/reactivate`);
      toast.success("User reactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-users-root">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Platform</div>
        <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Users</h1>
        <p className="text-[13px] text-[#667085] mt-0.5">Every human on a Fender garage tenant.</p>
      </div>

      <TableToolbar
        testIdPrefix="admin-users"
        searchValue={q}
        onSearchChange={setQ}
        searchPlaceholder="Search name, email, phone…"
        views={VIEWS}
        activeView={view}
        onViewChange={setView}
        columns={ALL_COLUMNS}
        visibleColumns={visibleCols}
        onToggleColumn={toggleCol}
        right={
          <select
            value={roleF}
            onChange={(e) => setRoleF(e.target.value)}
            data-testid="admin-users-role-filter"
            className="h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
          >
            {ROLE_FILTERS.map((r) => (
              <option key={r.k} value={r.k}>{r.label}</option>
            ))}
          </select>
        }
      />

      {/* Desktop */}
      <div className="hidden md:block bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-users-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              {isVisible("email") && <th className="text-left px-4 py-3 font-semibold">Email</th>}
              {isVisible("role") && <th className="text-left px-4 py-3 font-semibold">Role</th>}
              {isVisible("garage") && <th className="text-left px-4 py-3 font-semibold">Garage</th>}
              {isVisible("jc_week") && <th className="text-right px-4 py-3 font-semibold">JC / week</th>}
              {isVisible("last_seen") && <th className="text-left px-4 py-3 font-semibold">Created</th>}
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              {canModerate && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-[13px] text-[#667085]">
                  No users match this filter.
                </td>
              </tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="hover:bg-[#F7F9FC]" data-testid={`admin-user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="text-[13px] text-[#1d3557] font-semibold">{u.name || "—"}</div>
                  {u.phone && <div className="text-[11px] text-[#667085] font-mono">{u.phone}</div>}
                </td>
                {isVisible("email") && <td className="px-4 py-3 text-[12px] text-[#667085]">{u.email}</td>}
                {isVisible("role") && (
                  <td className="px-4 py-3 text-[11px] uppercase tracking-wider text-[#457b9d] font-semibold">
                    {u.role?.replace(/_/g, " ")}
                  </td>
                )}
                {isVisible("garage") && (
                  <td className="px-4 py-3">
                    {u.garage ? (
                      <Link
                        to={`/admin/garages/${u.garage.id}`}
                        data-testid={`admin-user-garage-${u.id}`}
                        className="inline-flex items-center gap-1 text-[12px] text-[#457b9d] hover:text-[#e63946] font-semibold"
                      >
                        {u.garage.name} <ArrowUpRight size={11} />
                      </Link>
                    ) : (
                      <span className="text-[12px] text-[#a8dadc]">—</span>
                    )}
                  </td>
                )}
                {isVisible("jc_week") && (
                  <td className="px-4 py-3 text-right font-mono text-[#1d3557]">{u.jc_this_week ?? 0}</td>
                )}
                {isVisible("last_seen") && (
                  <td className="px-4 py-3 text-[11px] font-mono text-[#667085]">
                    {u.created_at ? fmtDateTime(u.created_at) : "—"}
                  </td>
                )}
                <td className="px-4 py-3"><StatusPill active={!!u.active} /></td>
                {canModerate && (
                  <td className="px-4 py-3 text-right">
                    {u.active ? (
                      <button
                        onClick={() => setSuspendTarget(u)}
                        data-testid={`admin-user-suspend-${u.id}`}
                        className="text-[12px] text-[#e63946] hover:underline font-semibold"
                      >
                        Suspend
                      </button>
                    ) : (
                      <button
                        onClick={() => reactivate(u)}
                        data-testid={`admin-user-reactivate-${u.id}`}
                        className="text-[12px] text-[#12B76A] hover:underline font-semibold inline-flex items-center gap-1"
                      >
                        <RotateCcw size={11} /> Reactivate
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden space-y-2.5" data-testid="admin-users-cards">
        {items.map((u) => (
          <div key={u.id} data-testid={`admin-user-card-${u.id}`} className="bg-white rounded-lg border border-[#E4E7EC] p-3.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-[#1d3557] truncate">{u.name || u.email}</div>
                <div className="text-[11px] text-[#667085] mt-0.5 truncate">{u.email}</div>
                <div className="text-[11px] text-[#457b9d] mt-1 uppercase tracking-wider font-semibold">
                  {u.role?.replace(/_/g, " ")}{u.garage ? ` · ${u.garage.name}` : ""}
                </div>
              </div>
              <StatusPill active={!!u.active} />
            </div>
            {canModerate && (
              <div className="mt-3 pt-3 border-t border-[#F1FAEE] flex justify-end gap-3">
                {u.active ? (
                  <button
                    onClick={() => setSuspendTarget(u)}
                    className="text-[12px] text-[#e63946] hover:underline font-semibold"
                  >
                    Suspend
                  </button>
                ) : (
                  <button
                    onClick={() => reactivate(u)}
                    className="text-[12px] text-[#12B76A] hover:underline font-semibold inline-flex items-center gap-1"
                  >
                    <RotateCcw size={11} /> Reactivate
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Suspend Dialog */}
      <Dialog open={!!suspendTarget} onOpenChange={(v) => { if (!v) { setSuspendTarget(null); setReason(""); } }}>
        <DialogContent className="rounded-lg border-[#E4E7EC] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1d3557] font-semibold">
              Suspend {suspendTarget?.name || suspendTarget?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[12px] text-[#667085]">
              This user will not be able to log in until reactivated. A reason is required for the audit log.
            </p>
            <Textarea
              rows={3}
              data-testid="admin-user-suspend-reason"
              placeholder="Reason for suspension…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="rounded-md"
            />
            <Button
              onClick={suspend}
              data-testid="admin-user-suspend-confirm"
              className="w-full h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
            >
              <ShieldAlert size={14} className="mr-1.5" /> Confirm suspension
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
